import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Grid,
  TextField,
  MenuItem,
  Button,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import ExpensesList from '../components/ExpensesList';
import ExpenseForm from '../components/ExpenseForm';
import {
  createExpense,
  updateExpense,
  deleteExpense,
  listExpenses,
  exportExpenses as exportExpensesApi,
} from '../api/expenseApi';
import { getAllChauffeurs } from '../api/userApi';
import { getAllCamions } from '../api/camionApi';
import type { Expense, Chauffeur, Camion, DecodedToken, ExpenseType } from '../types';

const EXPENSE_TYPE_OPTIONS: { value: ExpenseType; label: string }[] = [
  { value: 'fuel', label: 'Carburant' },
  { value: 'maintenance', label: 'Entretien' },
  { value: 'repair', label: 'Réparations' },
  { value: 'welding', label: 'Soudure' },
  { value: 'wash', label: 'Lavage' },
  { value: 'tire', label: 'Pneus / Pièces' },
  { value: 'battery', label: 'Batteries' },
  { value: 'adblue', label: 'Ad Blue' },
  { value: 'toll', label: 'Péage' },
  { value: 'driver_pay', label: 'Paiement Chauffeur' },
  { value: 'other', label: 'Autre' },
];

const Expenses = () => {
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [totalExpenses, setTotalExpenses] = useState(0);
  const [chauffeurs, setChauffeurs] = useState<Chauffeur[]>([]);
  const [camions, setCamions] = useState<Camion[]>([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState<Partial<Expense>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [message, setMessage] = useState<{
    text: string;
    severity: 'success' | 'error';
  } | null>(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  // Filters
  const [start, setStart] = useState<string>('');
  const [end, setEnd] = useState<string>('');
  const [typeFilter, setTypeFilter] = useState<'' | ExpenseType>('');
  const [driverIdFilter, setDriverIdFilter] = useState<string>('');
  const [truckIdFilter, setTruckIdFilter] = useState<string>('');
  const [q, setQ] = useState<string>('');

  const navigate = useNavigate();

  const safeParseFloat = (value: any): number => {
    if (value === null || value === undefined || value === '') return 0;
    if (typeof value === 'number') return isNaN(value) ? 0 : value;
    if (typeof value === 'string') {
      const cleaned = value.replace(',', '.').trim();
      const parsed = parseFloat(cleaned);
      return isNaN(parsed) ? 0 : parsed;
    }
    return 0;
  };

  const ensureNumericExpense = (expense: any): Expense => {
    return {
      ...expense,
      amount: safeParseFloat(expense.amount),
      quantity: expense.quantity == null ? null : safeParseFloat(expense.quantity),
      type: expense.type || 'other',
      paymentMethod: expense.paymentMethod || 'cash',
      syncStatus: expense.syncStatus || 'pending',
      description: expense.description || '',
      location: expense.location || '',
      date: expense.date || new Date().toISOString(),
      driverName: expense.driverName || expense.driver?.nom || '',
    } as Expense;
  };

  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => setMessage(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }
    try {
      const decoded: DecodedToken = jwtDecode(token);
      if (decoded.role !== 'admin') {
        setError('Accès réservé aux administrateurs.');
        setLoading(false);
        return;
      }
      setIsAdmin(true);
    } catch (err) {
      console.error('Token Decoding Error:', err);
      navigate('/login');
      return;
    }

    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        const [listResp, chauffeursData, camionsData] = await Promise.all([
          listExpenses({
            limit: pageSize,
            offset: currentPage * pageSize,
            start: start || undefined,
            end: end || undefined,
            type: typeFilter || undefined,
            driverId: driverIdFilter || undefined,
            truckId: truckIdFilter || undefined,
            q: q || undefined,
          }),
          getAllChauffeurs(),
          getAllCamions(1, 1000),
        ]);

        if (!listResp || !Array.isArray(listResp.rows)) {
          console.error('Invalid expenses response format:', listResp);
          throw new Error('Format de réponse invalide pour les dépenses');
        }

        const processedExpenses = (listResp.rows as any[])
          .filter((expense) => expense && expense.id)
          .map(ensureNumericExpense);

        setExpenses(processedExpenses);
        setTotalExpenses(listResp.count || 0);

        if (Array.isArray(chauffeursData)) {
          const drivers = chauffeursData.filter((c) => c && c.role === 'driver');
          setChauffeurs(drivers);
        } else {
          setChauffeurs([]);
        }

        if (camionsData && Array.isArray(camionsData.camions)) {
          setCamions(camionsData.camions);
        } else {
          setCamions([]);
        }

        setLoading(false);
      } catch (err: any) {
        console.error('Fetch Error Details:', err);
        const errorMessage = err.response?.data?.message || err.message || 'Erreur lors du chargement des données.';
        setError(errorMessage);
        setExpenses([]);
        setChauffeurs([]);
        setCamions([]);
        setLoading(false);
      }
    };

    fetchData();
  }, [navigate, currentPage, pageSize, start, end, typeFilter, driverIdFilter, truckIdFilter, q]);

  const handleEditExpense = (expense: Expense) => {
    setIsEditing(true);
    setFormData({
      id: expense.id,
      driverId: expense.driverId,
      truckId: expense.truckId,
      type: expense.type,
      entryType: expense.entryType,
      date: expense.date,
      quantity: expense.quantity ?? undefined,
      amount: expense.amount,
      description: expense.description ?? undefined,
      location: expense.location ?? undefined,
      paymentMethod: expense.paymentMethod ?? undefined,
      imageUrl: expense.imageUrl ?? undefined,
      syncStatus: expense.syncStatus,
      periodMonth: expense.periodMonth,
      periodYear: expense.periodYear,
    });
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setFormData({});
    setIsEditing(false);
  };

  const handleSubmit = async (data: Partial<Expense>) => {
    try {
      if (isEditing && formData.id) {
        const processedData = {
          ...data,
          amount: safeParseFloat(data.amount),
          quantity: data.quantity == null ? null : safeParseFloat(data.quantity),
        } as Partial<Expense>;

        const updatedExpense = await updateExpense(formData.id, processedData);
        const processedUpdatedExpense = ensureNumericExpense(updatedExpense);

        setExpenses((prev) => prev.map((e) => (e.id === processedUpdatedExpense.id ? processedUpdatedExpense : e)));
        setMessage({ text: 'Dépense mise à jour avec succès.', severity: 'success' });
      } else {
        const processedData = {
          ...data,
          amount: safeParseFloat(data.amount),
          quantity: data.quantity == null ? null : safeParseFloat(data.quantity),
          entryType: data.entryType || 'manual',
        } as Partial<Expense>;

        const created = await createExpense(processedData);
        const processedCreated = ensureNumericExpense(created);
        setExpenses((prev) => [processedCreated, ...prev]);
        setTotalExpenses((prev) => prev + 1);
        setMessage({ text: 'Dépense créée avec succès.', severity: 'success' });
      }
      handleCloseDialog();
    } catch (err: any) {
      console.error('Submit Error Details:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Erreur lors de la soumission de la dépense.';
      setMessage({ text: errorMessage, severity: 'error' });
    }
  };

  const handleDeleteExpense = async (id: string) => {
    if (window.confirm('Voulez-vous vraiment supprimer cette dépense ?')) {
      try {
        await deleteExpense(id);
        setExpenses((prev) => prev.filter((e) => e.id !== id));
        setTotalExpenses((prev) => Math.max(0, prev - 1));
        setMessage({ text: 'Dépense supprimée avec succès.', severity: 'success' });
      } catch (err: any) {
        console.error('Delete Error Details:', err);
        const errorMessage = err.response?.data?.message || err.message || 'Erreur lors de la suppression de la dépense.';
        setMessage({ text: errorMessage, severity: 'error' });
      }
    }
  };

  const handleDownloadDocument = (url: string) => {
    if (url && url.trim() !== '') {
      window.open(url, '_blank');
    } else {
      setMessage({ text: 'Aucun document disponible pour téléchargement.', severity: 'error' });
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: name === 'quantity' || name === 'amount' ? (value === '' ? undefined : safeParseFloat(value)) : value,
    });
  };

  const handleSelectChange = (e: import('@mui/material').SelectChangeEvent<string>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleDateChange = (field: string, date: Date | null) => {
    setFormData({ ...formData, [field]: date ? date.toISOString() : null });
  };

  const handlePageChange = (event: unknown, newPage: number) => {
    setCurrentPage(newPage);
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newPageSize = parseInt(event.target.value, 10);
    setPageSize(newPageSize);
    setCurrentPage(0);
  };

  const resetFilters = () => {
    setStart(''); setEnd(''); setTypeFilter(''); setDriverIdFilter(''); setTruckIdFilter(''); setQ(''); setCurrentPage(0);
  };

  const exportExpenses = async (format: 'csv'|'xlsx'|'pdf') => {
    try {
      await exportExpensesApi({
        start: start || undefined,
        end: end || undefined,
        type: typeFilter || undefined,
        driverId: driverIdFilter || undefined,
        truckId: truckIdFilter || undefined,
        q: q || undefined,
      }, format);
    } catch (e: any) {
      setMessage({ text: e?.message || 'Erreur export', severity: 'error' });
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px', flexDirection: 'column', gap: 2 }}>
        <CircularProgress size={60} />
        <Typography variant="body1" color="textSecondary">Chargement des dépenses...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        <Typography variant="h6" gutterBottom>Erreur de chargement</Typography>
        {error}
      </Alert>
    );
  }

  if (!isAdmin) {
    return (
      <Alert severity="warning" sx={{ m: 2 }}>
        <Typography variant="h6" gutterBottom>Accès restreint</Typography>
        Accès réservé aux administrateurs.
      </Alert>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', m: 0 }}>
          Gestion des Dépenses
        </Typography>
        <Button
          variant="contained"
          onClick={() => {
            setIsEditing(false);
            setFormData({
              entryType: 'manual',
              date: new Date().toISOString(),
            } as Partial<Expense>);
            setOpenDialog(true);
          }}
        >
          Ajouter une dépense
        </Button>
      </Box>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={2}>
          <TextField type="date" label="Début" InputLabelProps={{ shrink: true }} value={start} onChange={(e) => setStart(e.target.value)} fullWidth />
        </Grid>
        <Grid item xs={12} md={2}>
          <TextField type="date" label="Fin" InputLabelProps={{ shrink: true }} value={end} onChange={(e) => setEnd(e.target.value)} fullWidth />
        </Grid>
        <Grid item xs={12} md={2}>
          <TextField select label="Type" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value as any)} fullWidth>
            <MenuItem value="">Tous</MenuItem>
            {EXPENSE_TYPE_OPTIONS.map((t) => (<MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>))}
          </TextField>
        </Grid>
        <Grid item xs={12} md={2}>
          <TextField select label="Chauffeur" value={driverIdFilter} onChange={(e) => setDriverIdFilter(e.target.value)} fullWidth>
            <MenuItem value="">Tous</MenuItem>
            {chauffeurs.map((c) => (<MenuItem key={c.id} value={c.id}>{c.nom}</MenuItem>))}
          </TextField>
        </Grid>
        <Grid item xs={12} md={2}>
          <TextField select label="Camion" value={truckIdFilter} onChange={(e) => setTruckIdFilter(e.target.value)} fullWidth>
            <MenuItem value="">Tous</MenuItem>
            {camions.map((c) => (<MenuItem key={c.id} value={c.id}>{c.nom}{c.immatriculation ? ` (${c.immatriculation})` : ''}</MenuItem>))}
          </TextField>
        </Grid>
        <Grid item xs={12} md={2}>
          <TextField label="Recherche" placeholder="Type, lieu, note..." value={q} onChange={(e) => setQ(e.target.value)} fullWidth />
        </Grid>
        <Grid item xs={12} md={12}>
          <Box display="flex" gap={1}>
            <Button variant="contained" onClick={() => setCurrentPage(0)}>Filtrer</Button>
            <Button variant="text" onClick={resetFilters}>Réinitialiser</Button>
            <Button variant="outlined" onClick={() => exportExpenses('csv')}>Export CSV</Button>
            <Button variant="outlined" onClick={() => exportExpenses('xlsx')}>Export Excel</Button>
            <Button variant="outlined" onClick={() => exportExpenses('pdf')}>Export PDF</Button>
          </Box>
        </Grid>
      </Grid>

      <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
        Total: {totalExpenses} dépense{totalExpenses !== 1 ? 's' : ''}
      </Typography>

      {message && (
        <Alert severity={message.severity} sx={{ mb: 3 }} onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}

      <ExpensesList
        expenses={expenses}
        chauffeurs={chauffeurs}
        handleEditExpense={handleEditExpense}
        handleDeleteExpense={handleDeleteExpense}
        handleDownloadDocument={handleDownloadDocument}
        currentPage={currentPage}
        pageSize={pageSize}
        totalExpenses={totalExpenses}
        handlePageChange={handlePageChange}
        handleRowsPerPageChange={handleRowsPerPageChange}
      />

      <ExpenseForm
        open={openDialog}
        isEditing={isEditing}
        formData={formData}
        chauffeurs={chauffeurs}
        camions={camions}
        handleInputChange={handleInputChange}
        handleSelectChange={handleSelectChange}
        handleDateChange={handleDateChange}
        handleCloseDialog={handleCloseDialog}
        handleSubmit={handleSubmit}
      />
    </Box>
  );
};

export default Expenses;
